/**
 * Souvenir PDF — served from REACT_APP_SOUVENIR_PDF_URL (Hostinger / CDN).
 * Storage bucket+path is a legacy fallback for download only.
 */

import { pdfViewerHref } from "./utils";

const DIRECT_URL = process.env.REACT_APP_SOUVENIR_PDF_URL?.trim();
const STORAGE_BUCKET = process.env.REACT_APP_SOUVENIR_PDF_BUCKET?.trim();
const STORAGE_OBJECT_PATH = process.env.REACT_APP_SOUVENIR_PDF_PATH?.trim();

export const souvenirPdfDownloadFilename =
  process.env.REACT_APP_SOUVENIR_PDF_FILENAME?.trim() ||
  "Sabha-Day-2026-Souvenir.pdf";

/** Absolute URL for PDF.js and fetch(); supports full https:// or site-root paths like /documents/foo.pdf */
export function getSouvenirPdfUrl() {
  if (!DIRECT_URL) return "";
  if (/^https?:\/\//i.test(DIRECT_URL)) return DIRECT_URL;
  if (DIRECT_URL.startsWith("/") && typeof window !== "undefined") {
    return `${window.location.origin}${DIRECT_URL}`;
  }
  return DIRECT_URL;
}

/** PDF.js viewer link for a sponsor page (uses env URL, not per-row Storage links). */
export function souvenirPdfViewerHref(pdfPage) {
  const url = getSouvenirPdfUrl();
  if (!url || !pdfPage) return "";
  return pdfViewerHref(url, pdfPage);
}

export function isSouvenirPdfViewerConfigured() {
  return Boolean(DIRECT_URL);
}

/** Show download control when a URL or Storage bucket+path is configured */
export function isSouvenirPdfDownloadConfigured(supabaseClientReady) {
  if (DIRECT_URL) return true;
  return Boolean(supabaseClientReady && STORAGE_BUCKET && STORAGE_OBJECT_PATH);
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * @param {import("@supabase/supabase-js").SupabaseClient | null} client
 */
export async function downloadSouvenirPdf(client) {
  const filename = souvenirPdfDownloadFilename;

  const fetchUrl = getSouvenirPdfUrl() || DIRECT_URL;
  if (fetchUrl) {
    const res = await fetch(fetchUrl);
    if (!res.ok) {
      throw new Error(`Could not download PDF (${res.status}). Check REACT_APP_SOUVENIR_PDF_URL.`);
    }
    const blob = await res.blob();
    triggerBlobDownload(blob, filename);
    return;
  }

  if (!client) {
    throw new Error("Supabase is not configured; add API keys or use REACT_APP_SOUVENIR_PDF_URL.");
  }
  if (!STORAGE_BUCKET || !STORAGE_OBJECT_PATH) {
    throw new Error("Set REACT_APP_SOUVENIR_PDF_BUCKET and REACT_APP_SOUVENIR_PDF_PATH in .env.");
  }

  const { data, error } = await client.storage
    .from(STORAGE_BUCKET)
    .download(STORAGE_OBJECT_PATH);

  if (error) {
    throw new Error(
      error.message ||
        "Could not download from Storage. Check bucket name, file path, and anon read policy."
    );
  }

  triggerBlobDownload(data, filename);
}
